import type { Loop, LoopPatch } from '../../services/loopService';

export type ScheduleMode = 'now' | 'later' | 'recurring';
export type ScheduleCadence = 'daily' | 'weekdays' | 'weekly' | 'custom';
export interface ScheduleDraft {
  mode: ScheduleMode;
  timezone: string;
  dateTime: string;
  cadence: ScheduleCadence;
  time: string;
  weekday: string;
  cron: string;
}

/** Short schedule text for loop lists ("Every weekday at 9:00", "Repeating", "One time"). */
export const scheduleLabel = (loop: Pick<Loop, 'schedule_json' | 'schedule_kind'>): string => loop.schedule_json.summary
  || (loop.schedule_kind === 'recurring' ? 'Repeating' : 'One time');

export const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const localScheduleTime = (date: Date, timezone: string): string => {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit',
    day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(date);
  const value = (name: string) => parts.find((part) => part.type === name)?.value;

  return `${value('year')}-${value('month')}-${value('day')}T${value('hour')}:${value('minute')}`;
};

/** Resolve wall-clock input in the selected zone; reject skipped or repeated DST times. */
// eslint-disable-next-line max-statements
export const scheduleInstant = (dateTime: string, timezone: string): Date => {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(dateTime)) throw new Error('Choose a date and time.');
  const wall = Date.parse(`${dateTime}:00Z`);

  if (!Number.isFinite(wall) || new Date(wall).toISOString().slice(0, 16) !== dateTime) {
    throw new Error('Choose a valid date and time.');
  }
  const offsets = [-86400000, 0, 86400000].map((shift) => {
    const reference = wall + shift;

    return Date.parse(`${localScheduleTime(new Date(reference), timezone)}:00Z`) - reference;
  });
  const candidates = Array.from(new Set(offsets)).map((offset) => new Date(wall - offset))
    .filter((candidate) => localScheduleTime(candidate, timezone) === dateTime);

  if (!candidates.length) throw new Error('That time is skipped by daylight saving. Choose another time.');
  if (candidates.length > 1) throw new Error('That time occurs twice due to daylight saving. Choose another time.');

  return candidates[0];
};

export const scheduleDraft = (loop: Loop): ScheduleDraft => {
  const cron = loop.schedule_json.cron || '0 9 * * *';
  const simple = /^(\d{1,2}) (\d{1,2}) \* \* (\*|1-5|[0-6])$/.exec(cron);
  const day = simple?.[3];

  return {
    mode: loop.schedule_kind === 'recurring' ? 'recurring' : loop.schedule_json.run_at ? 'later' : 'now',
    timezone: loop.timezone,
    dateTime: loop.schedule_json.run_at ? localScheduleTime(new Date(loop.schedule_json.run_at), loop.timezone) : '',
    cadence: !simple ? 'custom' : day === '*' ? 'daily' : day === '1-5' ? 'weekdays' : 'weekly',
    time: simple ? `${simple[2].padStart(2, '0')}:${simple[1].padStart(2, '0')}` : '09:00',
    weekday: day && /^[0-6]$/.test(day) ? day : '1',
    cron,
  };
};

const customSchedule = (draft: ScheduleDraft) => {
  const cron = draft.cron.trim().replace(/\s+/g, ' ');

  if (cron.split(' ').length !== 5) throw new Error('Use a five-field cron expression.');

  return { cron, summary: `Custom schedule: ${cron} (${draft.timezone})` };
};

const recurringSchedule = (draft: ScheduleDraft) => {
  if (draft.cadence === 'custom') return customSchedule(draft);
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(draft.time)) throw new Error('Choose a valid time.');
  if (!/^[0-6]$/.test(draft.weekday)) throw new Error('Choose a weekday.');
  const [hour, minute] = draft.time.split(':').map(Number);
  const day = draft.cadence === 'daily' ? '*' : draft.cadence === 'weekdays' ? '1-5' : draft.weekday;
  const label = draft.cadence === 'daily' ? 'Every day' : draft.cadence === 'weekdays'
    ? 'Every weekday' : `Every ${WEEKDAYS[Number(draft.weekday)]}`;

  return { cron: `${minute} ${hour} * * ${day}`, summary: `${label} at ${draft.time} (${draft.timezone})` };
};

// eslint-disable-next-line max-statements
export const schedulePatch = (draft: ScheduleDraft, now = new Date()): LoopPatch => {
  const timezone = draft.timezone.trim();

  try { new Intl.DateTimeFormat('en', { timeZone: timezone }).format(now); }
  catch { throw new Error('Use a valid timezone, such as Asia/Kolkata or America/New_York.'); }
  if (draft.mode === 'recurring') return { schedule_kind: 'recurring', timezone,
    schedule_json: recurringSchedule({ ...draft, timezone }) };
  if (draft.mode === 'now') return { schedule_kind: 'once', timezone,
    schedule_json: { run_at: null, summary: 'Run once now' } };
  const runAt = scheduleInstant(draft.dateTime, timezone);

  if (runAt <= now) throw new Error('Choose a future date and time.');

  return { schedule_kind: 'once', timezone, schedule_json: { run_at: runAt.toISOString(),
    summary: `Run once on ${draft.dateTime.replace('T', ' at ')} (${timezone})` } };
};
